import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Award, Leaf, TrendingUp, Mail } from 'lucide-react';

const AboutUs = () => {
  const values = [
    {
      icon: Heart,
      title: 'Quality First',
      description: 'We source only the finest materials and work with skilled craftsmen to ensure every piece meets our high standards.',
    },
    {
      icon: Users,
      title: 'Customer Focused',
      description: 'Your satisfaction is our priority. From browsing to delivery, we\'re here to make your experience exceptional.',
    },
    {
      icon: Award,
      title: 'Competitive Pricing',
      description: 'By operating online-first, we reduce overhead costs and pass the savings directly to you without compromising quality.',
    },
    {
      icon: Leaf,
      title: 'Sustainable Practices',
      description: 'We\'re committed to eco-friendly sourcing and packaging, ensuring our beautiful furniture doesn\'t cost the earth.',
    },
  ];

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '116+', label: 'Cities Served' },
    { number: '5,000+', label: 'Happy Customers' },
    { number: '99%', label: 'Satisfaction Rate' },
  ];

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm font-body">
            <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>
            <span className="text-dark-400">/</span>
            <span className="text-dark-700 font-semibold">About Us</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <h1 className="text-4xl text-dark-900 md:text-5xl lg:text-6xl font-display font-bold mb-6">
              About Sky Furniture
            </h1>
            <p className="text-xl md:text-2xl text-dark-700 max-w-3xl mx-auto font-body">
              Bringing quality furniture and exceptional service to homes across Ontario since 2014
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Our Story */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12 animate-slide-up">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-6 text-center">
              Our Story
            </h2>
            
            <div className="space-y-6 text-lg text-dark-700 font-body leading-relaxed">
              <p>
                Founded in 2014, Sky Furniture began with a simple vision: to make quality furniture accessible to every Canadian home. What started as a small family business in Toronto has grown into one of Ontario's trusted furniture retailers, serving over 116 cities across the province.
              </p>
              
              <p>
                We believe that your home is more than just a place—it's where memories are made, where families gather, and where life unfolds. That's why we're committed to providing furniture that's not only beautiful and affordable but also built to last for years to come.
              </p>
              
              <p>
                Our online-first approach allows us to cut out the middleman and pass those savings directly to you. But we never compromise on quality or service. Every piece in our collection is carefully selected for its craftsmanship, durability, and timeless design.
              </p>
              
              <p>
                Today, we're proud to have helped thousands of families furnish their homes with pieces they love. From modern minimalist designs to classic traditional styles, we offer something for every taste and budget.
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-card p-6 text-center hover:shadow-soft transition-smooth"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="text-4xl md:text-5xl font-display font-bold text-primary-700 mb-2">
                {stat.number}
              </div>
              <div className="text-dark-600 font-body font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-8 text-center">
            Our Values
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-card p-8 hover:shadow-soft transition-smooth"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-dark-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-dark-600 font-body leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-gradient-accent text-white rounded-2xl shadow-colored p-8 md:p-12 mb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-display font-bold mb-8 text-center">
              Why Choose Sky Furniture?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <TrendingUp className="w-8 h-8 mb-3" />
                <h3 className="font-display font-bold text-lg mb-2">Best Value</h3>
                <p className="text-accent-100 font-body text-sm">
                  Premium quality at competitive prices. We cut overhead, not corners.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <Award className="w-8 h-8 mb-3" />
                <h3 className="font-display font-bold text-lg mb-2">Quality Assured</h3>
                <p className="text-accent-100 font-body text-sm">
                  Every piece is inspected for quality before leaving our warehouse.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <Users className="w-8 h-8 mb-3" />
                <h3 className="font-display font-bold text-lg mb-2">Expert Service</h3>
                <p className="text-accent-100 font-body text-sm">
                  Our team is always ready to help with product selection and support.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <Heart className="w-8 h-8 mb-3" />
                <h3 className="font-display font-bold text-lg mb-2">Customer First</h3>
                <p className="text-accent-100 font-body text-sm">
                  Your satisfaction is guaranteed with our 30-day return policy.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Commitment */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <div className="max-w-3xl mx-auto text-center">
            <Leaf className="w-16 h-16 text-success mx-auto mb-6" />
            <h2 className="text-3xl font-display font-bold text-dark-900 mb-4">
              Our Commitment to Sustainability
            </h2>
            <p className="text-lg text-dark-700 font-body leading-relaxed mb-6">
              We believe in responsible business practices. That's why we partner with manufacturers who share our commitment to environmental sustainability. From FSC-certified wood to recyclable packaging materials, we're continuously working to reduce our environmental impact.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-success/10 rounded-xl">
                <p className="font-body font-semibold text-dark-900">♻️ Recyclable Packaging</p>
              </div>
              <div className="p-4 bg-success/10 rounded-xl">
                <p className="font-body font-semibold text-dark-900">🌳 Sustainable Sourcing</p>
              </div>
              <div className="p-4 bg-success/10 rounded-xl">
                <p className="font-body font-semibold text-dark-900">🚚 Efficient Delivery</p>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial */}
        <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12 mb-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-6xl text-primary-600 mb-4">"</div>
            <p className="text-xl md:text-2xl font-display text-dark-900 mb-6 italic">
              Sky Furniture helped us furnish our entire home with beautiful, high-quality pieces that fit our budget. The delivery team was professional, and the furniture exceeded our expectations!
            </p>
            <div className="flex items-center justify-center gap-2">
              <div className="flex text-warning">★★★★★</div>
            </div>
            <p className="text-dark-600 font-body mt-4">
              <strong>Sarah & Michael T.</strong> - Toronto, ON
            </p>
          </div>
        </div>

        {/* Join Our Journey */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-display font-bold text-dark-900 mb-4">
              Join Our Journey
            </h2>
            <p className="text-lg text-dark-600 font-body mb-6">
              Every piece of furniture tells a story, and we'd love to be part of yours. Whether you're furnishing your first apartment or redesigning your forever home, Sky Furniture is here to help you create a space you love.
            </p>
            <p className="text-lg text-dark-700 font-body font-semibold">
              Thank you for choosing Sky Furniture. Here's to making your house feel like home! 🏡
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-primary text-white rounded-xl font-body font-semibold text-lg hover:shadow-colored transition-smooth"
          >
            <Mail className="w-6 h-6" />
            Get in Touch
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;